import { motion } from "framer-motion";
import { Sparkles, Send } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function AICoach() {
  return (
    <section
      id="intelligence"
      className="relative py-24 md:py-32 overflow-hidden bg-zoku-bg"
      data-testid="ai-coach-section"
    >
      {/* Decorative glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-zoku-orangeLight blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <motion.div {...reveal} className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-1.5 text-xs font-semibold text-zoku-ink2">
            <Sparkles className="w-3.5 h-3.5 text-zoku-orange" />
            Zoku Intelligence
          </span>
          <h2 className="mt-6 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            A coach that <span className="font-editorial text-zoku-orange">reads your data</span>
            <br />
            — not Reddit.
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-xl mx-auto">
            Ask anything about your health. Zoku answers using your own numbers, cross-checked
            against peer-reviewed research.
          </p>
        </motion.div>

        {/* Chat mockup */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.15 }}
          className="mt-14 max-w-2xl mx-auto bg-white rounded-[2rem] border border-stone-100 shadow-widget p-6 md:p-8"
        >
          <div className="flex items-center gap-3 pb-4 border-b border-stone-100">
            <div className="w-10 h-10 rounded-full bg-zoku-orangeLight flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-zoku-orange" />
            </div>
            <div>
              <div className="font-semibold text-zoku-ink">Zoku Coach</div>
              <div className="text-xs text-emerald-600 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online
              </div>
            </div>
          </div>

          <div className="py-6 space-y-4">
            {/* User */}
            <div className="flex justify-end">
              <div className="bg-zoku-ink text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                I'm feeling not rested today. What happened last night?
              </div>
            </div>
            {/* AI */}
            <div className="flex">
              <div className="bg-zoku-orangeLight/60 text-zoku-ink rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] space-y-3">
                <p>
                  Your deep sleep was{" "}
                  <span className="font-semibold text-zoku-orange">45 min below</span> your 30-day
                  average. Two likely culprits:
                </p>
                <ul className="space-y-1.5 pl-2">
                  <li className="flex gap-2 text-sm">
                    <span className="text-zoku-orange font-bold">·</span>
                    Last coffee logged at 4:18 PM — caffeine half-life = ~6 hrs
                  </li>
                  <li className="flex gap-2 text-sm">
                    <span className="text-zoku-orange font-bold">·</span>
                    Strain was 18.4 — 23% higher than a typical Tuesday
                  </li>
                </ul>
                <p className="text-sm pt-1">
                  Try cutting caffeine after 2 PM this week. Want me to set a nudge?
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <button className="text-xs bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-zoku-orange transition">
                    Yes, set nudge
                  </button>
                  <button className="text-xs bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-zoku-orange transition">
                    Show me sources
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Input */}
          <div className="pt-4 border-t border-stone-100 flex items-center gap-2">
            <input
              disabled
              placeholder="Ask about your sleep, recovery, meals…"
              className="flex-1 bg-zoku-bg rounded-full px-4 py-3 text-sm outline-none border border-transparent focus:border-zoku-orange transition"
            />
            <button
              className="w-10 h-10 rounded-full bg-zoku-orange text-white flex items-center justify-center hover:bg-zoku-orangeHover transition"
              aria-label="Send"
              data-testid="ai-coach-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Feature chips */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.25 }}
          className="mt-10 grid sm:grid-cols-3 gap-4"
        >
          {[
            { t: "Proactive check-ins", d: "Daily summaries and nudges without you asking." },
            { t: "Research-grounded", d: "Cites PubMed, peer-reviewed journals. No influencer noise." },
            { t: "Your plan, your schedule", d: "Generates workouts and routines tailored to today." },
          ].map((c, i) => (
            <div
              key={i}
              className="bg-white/70 backdrop-blur rounded-2xl p-5 border border-stone-100"
            >
              <h4 className="font-semibold text-zoku-ink">{c.t}</h4>
              <p className="mt-1 text-sm text-zoku-ink2">{c.d}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
